import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Minus, Pencil, Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatCard } from "@/components/admin/ui";
import { TransactionDialog } from "@/components/admin/TransactionDialog";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { EmptyState } from "@/components/common";
import { api, fetcher, formatApiError, invalidateAdmin } from "@/lib/api";
import { formatShortDate, rupiah, todayISO } from "@/lib/format";

const TYPES = [["all", "Semua"], ["pemasukan", "Pemasukan"], ["pengeluaran", "Pengeluaran"]];

const TxRow = ({ t, onEdit, onDelete }) => (
  <TableRow className="border-white/5 hover:bg-white/[0.02]" data-testid={`tx-row-${t.id}`}>
    <TableCell className="whitespace-nowrap text-sm">{formatShortDate(t.date)}</TableCell>
    <TableCell>
      <span className={`rounded-full border px-2.5 py-1 text-xs ${t.type === "pemasukan" ? "border-emerald-400/30 text-emerald-300" : "border-rose-400/30 text-rose-300"}`}>
        {t.type === "pemasukan" ? "Pemasukan" : "Pengeluaran"}
      </span>
    </TableCell>
    <TableCell className="whitespace-nowrap">{t.category}</TableCell>
    <TableCell className="min-w-[200px]">
      <p className="text-sm">{t.description || "-"}</p>
      <p className="text-xs text-neutral-500">{[t.trip_name, t.source === "order" ? "Otomatis dari pesanan" : ""].filter(Boolean).join(" · ")}</p>
    </TableCell>
    <TableCell className={`whitespace-nowrap text-right font-mono ${t.type === "pemasukan" ? "text-emerald-300" : "text-rose-300"}`}>
      {t.type === "pemasukan" ? "+" : "−"}{rupiah(t.amount)}
    </TableCell>
    <TableCell>
      <div className="flex justify-end gap-1">
        <Button size="icon" variant="ghost" onClick={() => onEdit(t)} data-testid={`tx-edit-${t.id}`}><Pencil /></Button>
        <Button size="icon" variant="ghost" className="hover:text-rose-400" onClick={() => onDelete(t)} data-testid={`tx-delete-${t.id}`}><Trash2 /></Button>
      </div>
    </TableCell>
  </TableRow>
);

export default function Finance() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [type, setType] = useState("all");
  const [dialog, setDialog] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const { data } = useQuery({
    queryKey: ["admin-transactions", month, type],
    queryFn: () => fetcher("/admin/transactions", { month: month || undefined, type: type === "all" ? undefined : type }),
  });
  const refresh = () => invalidateAdmin(qc);
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/admin/transactions/${id}`),
    onSuccess: () => { toast.success("Transaksi dihapus"); setToDelete(null); refresh(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const s = data?.summary || { pemasukan: 0, pengeluaran: 0, saldo: 0 };
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="admin-finance-page">
      <PageHeader title="Keuangan" subtitle="Pemasukan, pengeluaran, dan saldo">
        <Button variant="outline" onClick={() => setDialog({ type: "pemasukan" })} data-testid="admin-keuangan-btn-add-income"><Plus /> Pemasukan</Button>
        <Button onClick={() => setDialog({ type: "pengeluaran" })} data-testid="admin-keuangan-btn-add"><Minus /> Pengeluaran</Button>
      </PageHeader>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="sm:w-48" data-testid="finance-filter-month" />
        <Button variant={month ? "ghost" : "secondary"} onClick={() => setMonth("")} data-testid="finance-filter-all-months">Semua bulan</Button>
        <div className="inline-flex rounded-full border border-white/10 p-1 text-xs sm:ml-auto">
          {TYPES.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setType(key)} data-testid={`finance-tab-${key}`}
              className={`rounded-full px-4 py-1.5 transition-colors ${type === key ? "bg-gold text-black" : "text-neutral-400 hover:text-white"}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard label="Pemasukan" value={rupiah(s.pemasukan)} tone="emerald" testId="finance-summary-income" />
        <StatCard label="Pengeluaran" value={rupiah(s.pengeluaran)} tone="rose" testId="finance-summary-expense" />
        <StatCard label="Saldo" value={rupiah(s.saldo)} tone={s.saldo >= 0 ? "gold" : "rose"} testId="finance-summary-balance" />
      </div>
      {items.length === 0 ? (
        <EmptyState icon={Wallet} title="Belum ada transaksi" text="Pembayaran pesanan otomatis tercatat di sini. Tambahkan pengeluaran seperti honor guide, transportasi, atau logistik." testId="finance-empty" />
      ) : (
        <div className="panel scroll-thin overflow-x-auto" data-testid="finance-table">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                {["Tanggal", "Jenis", "Kategori", "Keterangan", "Jumlah", ""].map((h) => (
                  <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-neutral-500 ${h === "Jumlah" ? "text-right" : ""}`}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>{items.map((t) => <TxRow key={t.id} t={t} onEdit={(x) => setDialog({ tx: x })} onDelete={setToDelete} />)}</TableBody>
          </Table>
        </div>
      )}
      {dialog && <TransactionDialog tx={dialog.tx} defaultType={dialog.type} onClose={() => setDialog(null)} onSaved={refresh} />}
      <ConfirmDelete open={!!toDelete} title="Hapus transaksi?" description="Transaksi ini akan dihapus permanen." onConfirm={() => remove.mutate(toDelete.id)} onCancel={() => setToDelete(null)} />
    </div>
  );
}
